import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Boxes,
  CalendarDays,
  ChevronLeft,
  ClipboardList,
  FileBarChart,
  FileText,
  FlaskConical,
  LayoutDashboard,
  Mail,
  Palette,
  Hash,
  PanelLeftClose,
  PanelLeftOpen,
  Pill,
  Presentation,
  ScrollText,
  ShieldCheck,
  Star,
  Stethoscope,
  Users,
  Settings,
  X,
} from "lucide-react";

import logo from "assets/bmsd-logo.png";
import { getUserRole } from "services/storage";

const navigationGroups = [
  {
    label: "Workspace",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard },
      { to: "/patients", label: "Patients", icon: Users },
      { to: "/book-appointment", label: "Appointments", icon: CalendarDays },
      { to: "/reports", label: "Reports", icon: FileBarChart },
    ],
  },
  {
    label: "Clinical",
    items: [
      { to: "/doctors", label: "Doctors", icon: Stethoscope },
      { to: "/panchakarma-therapies", label: "Panchakarma Therapies", icon: CalendarDays },
      { to: "/lab", label: "Investigations", icon: FlaskConical },
      { to: "/inventory", label: "Medicine Stocks", icon: Boxes },
      { to: "/addons", label: "Medicines & Diseases", icon: Pill },
      { to: "/billing-settings", label: "Billing Settings", icon: Settings },
    ],
  },
  {
    label: "Engagement",
    items: [
      { to: "/enquiries", label: "Enquiries", icon: Mail },
      { to: "/reviews", label: "Reviews", icon: Star },
      { to: "/mails", label: "Email", icon: FileText },
      { to: "/blogs", label: "Blogs", icon: ClipboardList },
      { to: "/register", label: "Registrations", icon: Presentation },
      {
        to: "/admin/webinar-registrations",
        label: "Webinar Leads",
        icon: Presentation,
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        to: "/accounts",
        label: "Accounts",
        icon: ShieldCheck,
        adminOnly: true,
      },
      {
        to: "/audit-logs",
        label: "Manager Activity",
        icon: ScrollText,
        adminOnly: true,
      },
      { to: "/patient-numbering", label: "Patient Numbering", icon: Hash },
      { to: "/colorsettings", label: "Appearance", icon: Palette },
    ],
  },
];

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const isAdmin = String(getUserRole() || "").toLowerCase() === "admin";
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("crmSidebarCollapsed") === "true";
  });

  useEffect(() => {
    localStorage.setItem("crmSidebarCollapsed", String(collapsed));
  }, [collapsed]);

  const closeOnMobile = () => {
    if (window.innerWidth < 1024 && toggleSidebar) toggleSidebar();
  };

  const linkClass = ({ isActive }) =>
    [
      "group relative flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm font-medium transition-all duration-200",
      collapsed ? "lg:justify-center" : "",
      isActive
        ? "crm-primary text-white shadow-sm"
        : "text-slate-600 hover:bg-[var(--accentColorLighter)] hover:text-slate-950",
    ].join(" ");

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={toggleSidebar}
          className="fixed inset-0 z-[80] bg-slate-950/35 backdrop-blur-[1px] lg:hidden"
        />
      )}

      <aside
        className={[
          "fixed inset-y-0 left-0 z-[90] flex h-screen flex-col border-r border-slate-200 bg-white transition-[width,transform] duration-300 lg:sticky lg:top-0 lg:translate-x-0",
          collapsed ? "lg:w-[88px]" : "lg:w-[268px]",
          "w-[280px]",
          isOpen ? "translate-x-0" : "-translate-x-full",
        ].join(" ")}
      >
        <div className="flex h-[72px] shrink-0 items-center justify-between border-b border-slate-200 px-4">
          <NavLink
            to="/"
            onClick={closeOnMobile}
            className={[
              "flex min-w-0 items-center gap-3",
              collapsed ? "lg:justify-center" : "",
            ].join(" ")}
          >
            <span className="crm-accent-icon grid h-11 w-11 shrink-0 place-items-center rounded-2xl ring-1 ring-[var(--accentColorBorder)]">
              <img
                src={logo}
                alt="BMSD Healthcare"
                className="h-9 w-9 object-contain"
              />
            </span>
            <span className={collapsed ? "lg:hidden" : "min-w-0"}>
              <strong className="block truncate text-[15px] font-semibold text-slate-950">
                BMSD CRM
              </strong>
              <span className="block truncate text-xs text-slate-500">
                Healthcare operations
              </span>
            </span>
          </NavLink>

          <button
            type="button"
            onClick={toggleSidebar}
            className="grid h-9 w-9 place-items-center rounded-xl text-slate-500 hover:bg-[var(--accentColorLighter)] hover:text-slate-900 lg:hidden"
            aria-label="Close sidebar"
          >
            <X size={19} />
          </button>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-5">
            {navigationGroups.map((group) => (
              <section key={group.label}>
                <p
                  className={[
                    "mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400",
                    collapsed ? "lg:hidden" : "",
                  ].join(" ")}
                >
                  {group.label}
                </p>

                <div className="space-y-1">
                  {group.items
                    .filter((item) => !item.adminOnly || isAdmin)
                    .map(({ to, label, icon: Icon }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === "/"}
                        className={linkClass}
                        onClick={closeOnMobile}
                        title={collapsed ? label : undefined}
                      >
                        <Icon size={19} className="shrink-0" />
                        <span className={collapsed ? "lg:hidden" : "truncate"}>
                          {label}
                        </span>
                      </NavLink>
                    ))}
                </div>
              </section>
            ))}
          </div>
        </nav>

        <div className="hidden shrink-0 border-t border-slate-200 p-3 lg:block">
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className={[
              "flex h-10 w-full items-center rounded-xl text-sm font-medium text-slate-500 hover:bg-[var(--accentColorLighter)] hover:text-slate-900",
              collapsed ? "justify-center" : "justify-between px-3",
            ].join(" ")}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : undefined}
          >
            {collapsed ? (
              <PanelLeftOpen size={19} />
            ) : (
              <>
                <span className="flex items-center gap-2">
                  <PanelLeftClose size={18} />
                  Collapse
                </span>
                <ChevronLeft size={17} />
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
